package com.tovtalk.data.repository

import com.tovtalk.data.network.retryOnTransient
import com.tovtalk.data.remote.ApiClient
import com.tovtalk.domain.repository.UploadRepository
import com.tovtalk.domain.repository.UploadResult
import com.tovtalk.domain.entity.DomainResult
import io.ktor.client.call.body
import io.ktor.http.isSuccess
import kotlin.time.Clock
import com.tovtalk.data.remote.dto.MediaUploadInitData
import com.tovtalk.data.remote.dto.MediaUploadCompleteData
import com.tovtalk.data.remote.dto.V2Response
import com.tovtalk.utils.logger.Logger

private data class PresignedData(val assetId: String, val uploadUrl: String, val filePath: String)

class UploadRepositoryImpl(
    private val apiClient: ApiClient
) : UploadRepository {

    /**
     * Idempotency guard for the upload "confirm" step.
     *
     * `confirmUpload` enqueues server-side processing for an [assetId]. If the
     * confirm HTTP response is lost (transient error) the caller retries — but the
     * server may already have processed it, so a naive retry double-processes the
     * asset. We record assetIds whose confirm has already succeeded in this process
     * and short-circuit any later confirm for the same asset. assetId is a stable,
     * single-use key (minted per init), so it's a safe idempotency token.
     */
    private val confirmedAssets = mutableSetOf<String>()
    private val confirmResults = mutableMapOf<String, UploadResult>()

    override suspend fun uploadFile(
        bytes: ByteArray,
        filename: String,
        category: String,
        onProgress: ((Float) -> Unit)?
    ): DomainResult<UploadResult> {
        return try {
            val uploadStart = com.tovtalk.utils.nowInstant()
            val contentType = getContentType(filename)
            val presignedResult = retryOnTransient(maxAttempts = 3) {
                getPresignedData(filename, contentType)
            }
            val uploadData = when (presignedResult) {
                is DomainResult.Success -> presignedResult.data
                is DomainResult.Error -> return presignedResult
                is DomainResult.Loading -> return DomainResult.Error("Unexpected loading state")
            }

            // 2. Upload directly to S3 (no retry - large payload)
            val uploadSuccess = apiClient.uploadToS3(
                url = uploadData.uploadUrl,
                bytes = bytes,
                contentType = contentType,
                onProgress = onProgress
            )
            if (!uploadSuccess) {
                return DomainResult.Error("Failed to upload file to storage")
            }

            // 3. Complete upload with Media API (enqueue processing)
            val confirmResult = retryOnTransient(maxAttempts = 3) {
                confirmUploadResult(uploadData.assetId, uploadData.filePath)
            }
            val result = when (confirmResult) {
                is DomainResult.Success -> confirmResult
                is DomainResult.Error -> confirmResult
                is DomainResult.Loading -> DomainResult.Error("Unexpected loading state")
            }
            val totalMs = com.tovtalk.utils.nowInstant().toEpochMilliseconds() - uploadStart.toEpochMilliseconds()
            Logger.d("uploadFile took ${totalMs}ms (size=${bytes.size})", "UploadRepositoryImpl")
            return result
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("uploadFile failed: ${e.message}", "UploadRepositoryImpl", e)
            e.printStackTrace()
            DomainResult.Error(e.message ?: "Unknown error", e)
        }
    }

    private suspend fun getPresignedData(filename: String, contentType: String): DomainResult<PresignedData> {
        return try {
            val presignedResponse = apiClient.getPresignedUploadUrl(filename, contentType)
            if (!presignedResponse.status.isSuccess()) {
                DomainResult.Error("Failed to get upload URL: ${presignedResponse.status.value}")
            } else {
                val presignedBody: V2Response<MediaUploadInitData> = presignedResponse.body()
                val initErrors = presignedBody.errors?.takeIf { it.isNotEmpty() }
                if (initErrors != null) {
                    DomainResult.Error(initErrors.first().message)
                } else {
                    val d = presignedBody.data ?: return DomainResult.Error("Failed to generate upload URL")
                    DomainResult.Success(PresignedData(d.assetId, d.upload.uploadUrl, d.upload.filePath))
                }
            }
        } catch (e: Exception) {
            DomainResult.Error(e.message ?: "Unknown error", e)
        }
    }

    private suspend fun confirmUploadResult(assetId: String, filePath: String): DomainResult<UploadResult> {
        // Idempotency: if this asset's confirm already succeeded (e.g. a previous
        // attempt's response was lost and we're now retrying), return the cached
        // result instead of re-confirming and double-enqueuing processing.
        if (assetId in confirmedAssets) {
            Logger.d("confirmUpload skipped (already confirmed): assetId=$assetId", "UploadRepositoryImpl")
            return DomainResult.Success(
                confirmResults[assetId] ?: UploadResult(url = filePath, path = filePath)
            )
        }
        return try {
            val confirmResponse = apiClient.confirmUpload(assetId = assetId)
            if (!confirmResponse.status.isSuccess()) {
                return DomainResult.Error("Failed to confirm upload: ${confirmResponse.status.value}")
            }
            val confirmBody: V2Response<MediaUploadCompleteData> = confirmResponse.body()
            val confirmErrors = confirmBody.errors?.takeIf { it.isNotEmpty() }
            if (confirmErrors != null) {
                DomainResult.Error(confirmErrors.first().message)
            } else {
                // UI still creates post from object-storage path.
                val result = UploadResult(url = filePath, path = filePath)
                // Record success so any retry for this assetId is a no-op.
                confirmedAssets.add(assetId)
                confirmResults[assetId] = result
                DomainResult.Success(result)
            }
        } catch (e: Exception) {
            DomainResult.Error(e.message ?: "Unknown error", e)
        }
    }

    private fun getContentType(filename: String): String {
        return when {
            filename.endsWith(".jpg", true) || filename.endsWith(".jpeg", true) -> "image/jpeg"
            filename.endsWith(".png", true) -> "image/png"
            filename.endsWith(".gif", true) -> "image/gif"
            filename.endsWith(".mp4", true) -> "video/mp4"
            filename.endsWith(".mov", true) -> "video/quicktime"
            filename.endsWith(".avi", true) -> "video/x-msvideo"
            else -> "application/octet-stream"
        }
    }
}
